import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { type FeedPost, shareToFeed } from '../client/ApiClient';

@customElement('share-modal')
export class ShareModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;
  @property({ type: String }) image: string | null = null;     // CDN URL
  @property({ type: String }) imageKey: string | null = null;  // preferred

  /** Show/hide side navigation arrows */
  @property({ type: Boolean }) showNav = true;

  @state() private caption = '';
  @state() private posting = false;
  @state() private error: string | null = null;

  // --- body scroll locking ---
  private _prevOverflow = '';
  private _prevPaddingRight = '';
  private _locked = false;
  private _preventScroll = (e: Event) => { e.preventDefault(); };

  connectedCallback(): void {
    super.connectedCallback();
    this.onKeyDown = this.onKeyDown.bind(this);
    window.addEventListener('keydown', this.onKeyDown);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.onKeyDown);
    this.unlockBodyScroll();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('open')) {
      if (this.open) this.lockBodyScroll();
      else this.unlockBodyScroll();
    }
  }

  private lockBodyScroll() {
    if (this._locked) return;
    const doc = document.documentElement;
    const body = document.body;
    const scrollBarWidth = window.innerWidth - doc.clientWidth;

    this._prevOverflow = body.style.overflow || '';
    this._prevPaddingRight = body.style.paddingRight || '';

    body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      body.style.paddingRight = `calc(${getComputedStyle(body).paddingRight} + ${scrollBarWidth}px)`;
    }
    document.addEventListener('touchmove', this._preventScroll, { passive: false });
    this._locked = true;
  }
  private unlockBodyScroll() {
    if (!this._locked) return;
    const body = document.body;
    body.style.overflow = this._prevOverflow;
    body.style.paddingRight = this._prevPaddingRight;
    document.removeEventListener('touchmove', this._preventScroll);
    this._locked = false;
  }

  private onKeyDown(e: KeyboardEvent) {
    if (!this.open) return;
    if (e.key === 'Escape') this.close();
    if (e.key === 'ArrowLeft') this.prev();
    if (e.key === 'ArrowRight') this.next();
  }

  private close() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  // ---- Navigation: emit events for parent to handle ----
  private prev() { this.dispatchEvent(new CustomEvent('prev', { bubbles: true, composed: true })); }
  private next() { this.dispatchEvent(new CustomEvent('next', { bubbles: true, composed: true })); }

  // ---- Share actions ----
  private async doShare() {
    if (!this.image && !this.imageKey) return;
    this.posting = true;
    this.error = null;
    try {
      const post: FeedPost = await shareToFeed({
        image_key: this.imageKey || undefined,
        image_url: this.image || undefined,
        caption: this.caption,
      });
      alert('Shared to feed 🎉');
      this.close();
    } catch (e: any) {
      this.error = e?.message || 'Failed to share';
    } finally {
      this.posting = false;
    }
  }

  private async nativeShare() {
    if (this.image && (navigator as any).share) {
      try {
        await (navigator as any).share({ title: 'Check this look', text: this.caption, url: this.image });
        return;
      } catch { /* fall through */ }
    }
    await this.copyLink();
  }

  private async copyLink() {
    if (!this.image) return;
    try {
      await navigator.clipboard.writeText(this.image);
      alert('Link copied to clipboard');
    } catch {
      alert('Copy failed—long-press to copy the URL.');
    }
  }

  // ---- UI ----
  private navButton(side: 'left' | 'right') {
    const isLeft = side === 'left';
    const click = isLeft ? () => this.prev() : () => this.next();
    const icon = isLeft
      ? 'M15.75 19.5 8.25 12l7.5-7.5'  // heroicons chevron-left
      : 'M8.25 4.5 15.75 12l-7.5 7.5'; // heroicons chevron-right
    const label = isLeft ? 'Previous (←)' : 'Next (→)';
    return html`
      <button
        class="absolute top-1/2 -translate-y-1/2 ${isLeft ? 'left-2 sm:left-3' : 'right-2 sm:right-3'}
               grid place-items-center h-10 w-10 sm:h-12 sm:w-12 rounded-full
               bg-black/40 hover:bg-black/60 border border-white/20 backdrop-blur
               text-white transition"
        @click=${click}
        aria-label=${label}
        title=${label}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
             class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d=${icon} />
        </svg>
      </button>
    `;
  }

  render() {
    if (!this.open) return null;

    return html`
      <!-- Backdrop -->
      <div
        class="fixed inset-0 z-[90] bg-black/70"
        @click=${this.close}
        @wheel=${this._preventScroll}
        @touchmove=${this._preventScroll}
      ></div>

      <!-- Shell -->
      <div class="fixed inset-0 z-[100] p-3 sm:p-4 md:p-6 grid place-items-center">
        <div
          class="relative w-full h-full max-w-7xl max-h-[95vh] rounded-2xl border border-white/10
                 bg-slate-950/90 backdrop-blur overflow-hidden"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <!-- Top bar -->
          <div class="absolute top-0 left-0 right-0 z-10 p-2 sm:p-3 flex items-center justify-between
                      bg-gradient-to-b from-black/60 to-transparent">
            <div class="text-sm sm:text-base font-semibold text-white/90">Share</div>
            <button class="rounded-lg border border-white/15 bg-white/10 hover:bg-white/20 px-3 py-1.5"
                    @click=${this.close}>✕</button>
          </div>

          <!-- Main area: image (left) + caption/actions (right) -->
          <div class="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-3 md:gap-4 h-full pt-10 pb-4 px-2 sm:px-4">
            <!-- Image zone -->
            <div class="relative h-full rounded-xl overflow-hidden bg-black/30">
              <div class="w-full h-full grid place-items-center overflow-auto">
                ${this.image
                  ? html`<img src=${this.image} class="max-h-full max-w-full object-contain" alt="Generated image" />`
                  : html`<div class="text-slate-400 text-sm">No preview</div>`}
              </div>

              <!-- Side arrows -->
              ${this.showNav ? this.navButton('left') : null}
              ${this.showNav ? this.navButton('right') : null}
            </div>

            <!-- Right column: caption + share -->
            <div class="h-full flex flex-col">
              <div class="flex-1 overflow-auto rounded-xl border border-white/10 bg-white/5 p-3">
                <div class="text-sm text-slate-300 mb-2">Caption (optional)</div>
                <textarea
                  class="w-full h-40 rounded-xl bg-slate-900/60 border border-white/10 px-3 py-2 text-sm text-slate-100
                         placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40"
                  placeholder="Add a short caption…"
                  .value=${this.caption}
                  maxlength="200"
                  @input=${(e: any) => (this.caption = e.target.value)}
                ></textarea>
                <div class="mt-1 text-[11px] text-slate-400 text-right">${this.caption.length}/200</div>

                ${this.error ? html`
                  <div class="mt-2 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                    ${this.error}
                  </div>
                ` : null}
              </div>

              <div class="mt-3 flex items-center gap-2">
                <button
                  class="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-400/30
                         bg-emerald-500/20 hover:bg-emerald-500/30 px-4 py-2.5 font-medium disabled:opacity-50"
                  @click=${this.doShare}
                  ?disabled=${this.posting}
                >
                  ${this.posting ? 'Sharing…' : 'Share to Feed'}
                </button>

                <button
                  class="rounded-xl border border-white/10 bg-white/10 hover:bg-white/20 px-3 py-2 text-sm"
                  @click=${this.nativeShare}
                >Share…</button>

                ${this.image ? html`
                  <button
                    class="rounded-xl border border-white/10 bg-white/10 hover:bg-white/20 px-3 py-2 text-sm"
                    @click=${this.copyLink}
                  >Copy link</button>
                ` : null}
              </div>

              <div class="mt-2 text-[11px] text-slate-400">
                Tip: Tap & hold the image to save, then post anywhere.
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

